// Rules of the evening planning (spec AV1, AV2, RO4 AC3/AC4). Pure: the device runs the flow offline.

import { addDays, isoWeekday } from "./time";
import { weekStart, weekTarget, type CompletionLike, type RoutineEntryLike, type RoutineLike } from "./routines";

export type ChecklistCondition = { kind: "always" } | { kind: "sport"; titles?: string[] } | { kind: "day_type"; template_id: string };

export interface ChecklistItemLike {
  id: string;
  title: string;
  condition: ChecklistCondition;
  position: number;
  deleted_at: string | Date | null;
}

export interface DayFacts {
  /** The day type of that day. */
  templateId: string | null;
  /** A routine with a sport block stands on it (the gym). */
  sportRoutine: boolean;
  /** Titles of the appointments that day (padel counts as sport when its title is in the item's list). */
  appointmentTitles: string[];
}

/** Klaarleggen for a day (AV2): what applies, in order. */
export function checklistFor<T extends ChecklistItemLike>(items: T[], day: DayFacts): T[] {
  const titles = new Set(day.appointmentTitles.map((t) => t.toLowerCase()));
  return items
    .filter((i) => !i.deleted_at)
    .filter((i) => {
      const c = i.condition;
      if (c.kind === "always") return true;
      if (c.kind === "day_type") return c.template_id === day.templateId;
      return day.sportRoutine || (c.titles ?? []).some((t) => titles.has(t.toLowerCase()));
    })
    .sort((a, b) => a.position - b.position);
}

export interface GymOffer {
  /** The day the backup would go on. */
  date: string;
  /** How many the week still needs (at most this many backups, RO4 AC4). */
  needed: number;
}

/**
 * The gym backup question (RO4 AC3): on Friday evening for Saturday, on Saturday evening for Sunday, and in the
 * morning ("Snel plannen", AV1 AC6) on Saturday for Saturday itself. Only while the week is short, never on a
 * Ziek or Vakantie day, and never when the gym already stands on that day.
 */
export function gymOffer(
  routine: RoutineLike,
  planningFor: string,
  completions: CompletionLike[],
  entries: RoutineEntryLike[],
  isPaused: (date: string) => boolean,
): GymOffer | null {
  if (!routine.weekly_target || isPaused(planningFor)) return null;
  const weekday = isoWeekday(planningFor);
  if (weekday !== 6 && weekday !== 7) return null;
  const monday = weekStart(planningFor);
  const sunday = addDays(monday, 6);
  const mine = entries.filter((e) => e.routine_id === routine.id && !e.deleted_at && e.plan_date >= monday && e.plan_date <= sunday);
  if (mine.some((e) => e.plan_date === planningFor && (e.status === "planned" || e.status === "done"))) return null;
  const done = completions.filter((c) => c.routine_id === routine.id && !c.deleted_at && c.done_on >= monday && c.done_on <= sunday).length;
  const ahead = mine.filter((e) => e.status === "planned" && e.plan_date >= planningFor).length;
  const needed = weekTarget(routine, planningFor, isPaused) - done - ahead;
  return needed > 0 ? { date: planningFor, needed } : null;
}
